import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface ErrorBannerProps {
  /** User-facing message. When falsy, the banner renders nothing. */
  message?: string | null;
  /** Retry handler — shows a Retry button when provided. */
  onRetry?: () => void;
  /** Disable the retry button (e.g. while a refetch is in flight). */
  retrying?: boolean;
  className?: string;
}

/**
 * ErrorBanner — the standard "a fetch failed" affordance for data screens.
 *
 * Wiz-parity rule: a load failure must NOT masquerade as an empty result.
 * Pages that fetch data should keep an `error` state, set it in the catch,
 * clear it on load/refresh, and render this banner so an outage reads as
 * "couldn't load — retry" instead of "no data".
 */
const ErrorBanner: React.FC<ErrorBannerProps> = ({ message, onRetry, retrying, className = '' }) => {
  if (!message) return null;
  return (
    <div
      role="alert"
      className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-200 ${className}`}
    >
      <span className="flex items-center gap-2 min-w-0">
        <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
        <span className="truncate">{message}</span>
      </span>
      {onRetry && (
        <button
          onClick={onRetry}
          disabled={retrying}
          className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-red-500/20 hover:bg-red-500/30 text-red-100 text-xs font-medium transition-colors disabled:opacity-50 shrink-0"
        >
          <RefreshCw className={`w-3 h-3 ${retrying ? 'animate-spin' : ''}`} aria-hidden="true" /> Retry
        </button>
      )}
    </div>
  );
};

export default ErrorBanner;
